import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import {
  ICreateCredentialDto,
  IUpdateCredentialDto,
} from 'common/src/types/credential.dtos';
import { CredentialType } from 'common/src/types/enums/CredentialType.enum';
import { ICredential, IPage, IUtility } from 'common/src/types/interfaces';

import { QUERY_KEYS } from 'portal/src/api/constants';
import {
  addCredential,
  updateCredential,
} from 'portal/src/api/CredentialsService';
import { getUtilities } from 'portal/src/api/UtilitiesService';
import useAuth from 'portal/src/auth/useAuth';
import { FormContainer } from 'portal/src/components/common/forms/FormContainer';
import { prepareFormPayload } from 'portal/src/components/common/forms/handleFormData';
import useFormControlError from 'portal/src/components/common/forms/useFormControlError';
import TimestampDisplay from 'portal/src/components/common/TimestampDisplay';
import { MESSAGES } from 'portal/src/constants/snackbarMessages';
import useSnackbarHandler from 'portal/src/utils/Snackbar/useSnackbarHandler';

import {
  CredentialFormValues,
  defaultValues,
  getCredentialFormFields,
} from './UtilityCredentialsForm.fields';

import ChangePasswordForm from '../ChangePassword/ChangePasswordForm';

const UtilityCredentialsForm = ({
  entity = {} as Partial<ICredential>,
  handleBackAction,
}: {
  entity?: Partial<ICredential>;
  handleBackAction: () => void;
}) => {
  const snackbarHandler = useSnackbarHandler();
  const { getUser } = useAuth();
  const { organizationId } = getUser();

  const [showChangePwDialog, setShowChangePwDialog] = useState(false);

  const {
    control,
    formState: { dirtyFields, isDirty },
    handleSubmit,
    getValues,
    setError,
  } = useForm({ defaultValues: defaultValues(entity) });
  const { getSnackbarApiErrorMessage } = useFormControlError({
    getValues,
    setError,
  });
  const { data: utilities } = useQuery({
    queryKey: [QUERY_KEYS.utilities],
    queryFn: async () => await getUtilities(),
    initialData: { data: [] } as IPage<IUtility>,
  });

  const { mutate: mutateCredential, isPending } = useMutation({
    mutationFn: async (formValues: CredentialFormValues) => {
      const payload: ICreateCredentialDto | IUpdateCredentialDto = {
        ...prepareFormPayload(formValues, dirtyFields),
        type: CredentialType.Utility,
        organizationId,
      };

      if (entity.id) {
        await updateCredential(entity.id, payload);
        snackbarHandler.success(MESSAGES.CREDENTIAL.UPDATED);
      } else {
        await addCredential(payload as ICreateCredentialDto);
        snackbarHandler.success(MESSAGES.CREDENTIAL.CREATED);
      }

      handleBackAction();
    },
    onError: (err: any) =>
      snackbarHandler.error(getSnackbarApiErrorMessage(err)),
  });

  const formFieldsConfig = getCredentialFormFields(utilities.data, !entity.id);

  const credentialHeader = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        mb: 3,
      }}
    >
      <Typography variant="h6">Credential Information</Typography>
      {entity.invalidAt && (
        <Stack sx={{ display: 'flex', flexDirection: 'column' }}>
          <Chip
            icon={<ErrorOutlineOutlinedIcon />}
            color="error"
            label="Invalid Credential"
          />
          <TimestampDisplay date={entity.invalidAt} prefix="Invalidated at " />
        </Stack>
      )}
    </Box>
  );

  return (
    <FormContainer
      control={control}
      isDirty={isDirty}
      extraButtons={
        entity.id
          ? [
              {
                onClick: () => setShowChangePwDialog(true),
                label: 'Change Password',
              },
            ]
          : undefined
      }
      handleBackAction={handleBackAction}
      handleSubmit={handleSubmit((data) => mutateCredential(data))}
      header={credentialHeader}
      isNew={!entity.id}
      isPending={isPending}
      mainFieldsConfig={formFieldsConfig}
      afterMainFields={
        <ChangePasswordForm
          open={showChangePwDialog}
          onClose={() => setShowChangePwDialog(false)}
          credentialId={entity.id}
        />
      }
    />
  );
};

export default UtilityCredentialsForm;
